import { useState } from "react";
import { toast } from "sonner";
import { Play, MonitorSmartphone } from "lucide-react";
import { Reveal } from "./Reveal";
import { SectionHeading } from "./Section";
import { TUTORIAL_VIDEO_URL } from "@/config/landing";

function getYouTubeId(url) {
  const m = url.match(/(?:youtu\.be\/|[?&]v=)([\w-]{6,})/);
  return m ? m[1] : null;
}

export function Tutorial() {
  const [playing, setPlaying] = useState(false);
  const videoId = TUTORIAL_VIDEO_URL ? getYouTubeId(TUTORIAL_VIDEO_URL) : null;

  const handlePlay = () => {
    if (videoId) {
      setPlaying(true);
    } else if (TUTORIAL_VIDEO_URL) {
      window.open(TUTORIAL_VIDEO_URL, "_blank", "noopener,noreferrer");
    } else {
      toast.info("Video panduan belum tersedia", {
        description:
          "Tautan video demonstrasi akan dipasang setelah URL asli diberikan oleh pemilik.",
      });
    }
  };

  return (
    <section className="bg-white py-16 sm:py-24">
      <div className="mx-auto max-w-6xl px-4 sm:px-6 lg:px-8">
        <Reveal>
          <SectionHeading
            title="Tidak Harus Menguasai Akuntansi untuk Mulai Menghitung"
            lead="KalkuBisnis dirancang untuk membantu kamu memahami perhitungan usaha melalui data yang kamu masukkan. Pelajari fitur-fiturnya secara bertahap menggunakan video panduan yang disertakan dalam pembelian."
          />
        </Reveal>
        <Reveal delay={0.15}>
          <div className="relative mx-auto mt-12 w-full max-w-3xl overflow-hidden rounded-2xl border border-slate-200 bg-slate-900 shadow-xl">
            {playing && videoId ? (
              <div className="aspect-video w-full">
                <iframe
                  src={`https://www.youtube.com/embed/${videoId}?rel=0`}
                  title="Video panduan penggunaan KalkuBisnis"
                  allow="accelerometer; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
                  allowFullScreen
                  className="h-full w-full"
                />
              </div>
            ) : (
              <button
                type="button"
                data-testid="tutorial-play-button"
                onClick={handlePlay}
                aria-label="Putar video panduan KalkuBisnis"
                className="group relative block w-full text-left focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-teal-600"
              >
                <div className="relative aspect-video w-full bg-slate-900">
                  {videoId && (
                    <img
                      src={`https://i.ytimg.com/vi/${videoId}/hqdefault.jpg`}
                      alt="Cuplikan video panduan penggunaan KalkuBisnis"
                      width={480}
                      height={360}
                      loading="lazy"
                      decoding="async"
                      className="absolute inset-0 h-full w-full object-cover opacity-70"
                    />
                  )}
                  <div className="absolute inset-0 bg-gradient-to-t from-slate-900/80 via-transparent to-slate-900/30" />
                  <div className="absolute inset-0 flex flex-col items-center justify-center gap-4 p-6 text-center">
                    <span className="flex h-16 w-16 items-center justify-center rounded-full bg-teal-600 text-white shadow-lg shadow-teal-600/40 transition-transform duration-200 group-hover:scale-110">
                      <Play className="ml-1 h-7 w-7" aria-hidden="true" />
                    </span>
                    <p className="text-sm font-semibold text-white">Video panduan penggunaan</p>
                    <p className="max-w-sm text-xs leading-relaxed text-slate-300">
                      Termasuk dalam pembelian — bukan produk terpisah. Klik untuk memutar.
                    </p>
                  </div>
                </div>
              </button>
            )}
          </div>
        </Reveal>
        <Reveal delay={0.2}>
          <p className="mt-6 flex items-center justify-center gap-1.5 text-center text-xs text-slate-500">
            <MonitorSmartphone className="h-3.5 w-3.5 text-teal-600" aria-hidden="true" />
            KalkuBisnis diakses lewat browser di HP atau komputer, tanpa instalasi.
          </p>
        </Reveal>
      </div>
    </section>
  );
}
